import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { isNullish } from 'remeda';
import { useCurrentUserProfile } from 'src/graphql';
import { useAiMessageComposition } from 'src/graphql/entities/ai-message-composition';
import { parseAiMarkdownToTipTapHtml } from 'src/pages/Conversations/Chat/ai-markdown-to-tiptap-html';
import {
  AiGenerationError,
  AiGenerationRequest,
  classifyAiGenerationError,
} from './ai-generation';
import { preserveSecurityUrgencyHeader } from './preserve-security-urgency-header';

export const useAiAssistant = ({
  setContent,
  getCurrentEditorContent,
  conversationId,
  conversationPartnerId,
  generationScope = conversationId,
}: {
  setContent?: (value: string) => void;
  getCurrentEditorContent?: () => string;
  conversationId: string;
  conversationPartnerId: number;
  generationScope?: string;
}) => {
  const [isGeneratingAiSuggestion, setIsGeneratingAiSuggestion] = useState(false);
  const [pendingAiSuggestion, setPendingAiSuggestion] = useState('');
  const [currentUserInput, setCurrentUserInput] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [previousResponseId, setPreviousResponseId] = useState('');
  const [selectedPromptName, setSelectedPromptName] = useState('');
  const [conversationMessageId, setConversationMessageId] = useState<string | undefined>();
  const [aiGenerationError, setAiGenerationError] = useState<AiGenerationError | null>(null);
  // Captured when a generation is dispatched so a retry does not depend on live
  // editor or picker state, which the agent may have changed since.
  const lastRequestRef = useRef<AiGenerationRequest | null>(null);
  const generationSequenceRef = useRef(0);
  const { getAiMessageComposition } = useAiMessageComposition();
  const { user } = useCurrentUserProfile();

  const handleIncomingAiSuggestion = useCallback(
    async ({
      message,
      responseId,
      generationSequence,
      currentDraftContent,
      preserveCurrentContext,
    }: {
      message: string;
      responseId: string | undefined;
      generationSequence: number;
      currentDraftContent?: string;
      preserveCurrentContext: boolean;
    }) => {
      const parsedContent = await parseAiMarkdownToTipTapHtml(message);
      if (generationSequence !== generationSequenceRef.current) return;

      setIsGeneratingAiSuggestion(false);
      if (message && setContent) {
        const content = preserveCurrentContext
          ? preserveSecurityUrgencyHeader(currentDraftContent || '', parsedContent)
          : parsedContent;
        setContent(content);
        setPendingAiSuggestion(message);

        if (responseId && responseId !== previousResponseId) {
          setPreviousResponseId(responseId || '');
        }
      }
    },
    [previousResponseId, setContent],
  );

  const handleGenerationStarted = useCallback(
    (opts?: {
      promptId?: string;
      promptName?: string;
      customPrompt?: string;
      userInput?: string;
    }) => {
      const { promptId, promptName, customPrompt, userInput } = opts || {};
      setCustomPrompt(customPrompt || '');
      setSelectedPrompt(promptId || '');
      setSelectedPromptName(promptName || '');
      setCurrentUserInput(userInput || '');
      setIsGeneratingAiSuggestion(true);
      setPendingAiSuggestion('');
    },
    [],
  );

  /**
   * Runs one composition. Nothing here touches the editor or the macro actions
   * until a draft actually comes back, which is what makes a retry free.
   */
  const runComposition = useCallback(
    async (request: AiGenerationRequest): Promise<void> => {
      const generationSequence = ++generationSequenceRef.current;
      lastRequestRef.current = request;
      setAiGenerationError(null);
      setIsGeneratingAiSuggestion(true);

      try {
        const newSuggestion = await getAiMessageComposition({
          partnerId: conversationPartnerId,
          userId: request.userId,
          conversationId,
          conversationMessageId: request.conversationMessageId,
          promptId: request.promptId,
          customPrompt: request.customPrompt,
          userInput: request.userInput,
          correction: request.correction,
          previousResponseId: request.previousResponseId,
        });

        if (newSuggestion?.error) {
          throw newSuggestion.error;
        }

        const { responseId: newResponseId, aiMessage } =
          newSuggestion?.data?.getAiMessageComposition || {};

        if (!aiMessage) {
          // An empty draft is indistinguishable from a failure to the agent, so
          // it is reported as one instead of clearing the loader silently.
          throw new Error('The assistant returned an empty draft');
        }

        await handleIncomingAiSuggestion({
          message: aiMessage,
          responseId: newResponseId,
          generationSequence,
          currentDraftContent: request.correction ? request.userInput : undefined,
          preserveCurrentContext: Boolean(request.correction),
        });
      } catch (error) {
        if (generationSequence !== generationSequenceRef.current) return;
        console.error(error);
        setIsGeneratingAiSuggestion(false);
        setAiGenerationError(classifyAiGenerationError(error));
      }
    },
    [
      conversationId,
      conversationPartnerId,
      getAiMessageComposition,
      handleIncomingAiSuggestion,
    ],
  );

  const getInitialSuggestion = useCallback(
    async ({
      promptId,
      promptName,
      customPrompt,
      userId,
      userInput,
    }: {
      promptId?: string;
      promptName?: string;
      customPrompt?: string;
      userId?: string;
      userInput?: string;
    }): Promise<void> => {
      if (isNullish(userId)) return;

      const generatedConversationMessageId = crypto.randomUUID();
      setConversationMessageId(generatedConversationMessageId);
      handleGenerationStarted({ promptId, promptName, customPrompt, userInput });

      await runComposition({
        userId,
        promptId,
        promptName,
        customPrompt,
        userInput,
        conversationMessageId: generatedConversationMessageId,
      });
    },
    [handleGenerationStarted, runComposition, setConversationMessageId],
  );

  const handleRefineSuggestion = useCallback(
    async (correction: string) => {
      if (!correction || isNullish(user?.id)) return;

      const ensuredConversationMessageId = conversationMessageId ?? crypto.randomUUID();
      const currentEditorContent = getCurrentEditorContent?.();
      if (!conversationMessageId) {
        setConversationMessageId(ensuredConversationMessageId);
      }

      await runComposition({
        userId: user.id,
        userInput: currentEditorContent || pendingAiSuggestion,
        conversationMessageId: ensuredConversationMessageId,
        correction,
        customPrompt,
        promptId: selectedPrompt,
        previousResponseId,
      });
    },
    [
      customPrompt,
      conversationMessageId,
      getCurrentEditorContent,
      pendingAiSuggestion,
      previousResponseId,
      runComposition,
      selectedPrompt,
      setConversationMessageId,
      user?.id,
    ],
  );

  /**
   * Replays the last dispatched request verbatim. Only offered for retryable
   * failures; an unavailable prompt would fail identically every time.
   */
  const retryGeneration = useCallback(async () => {
    const lastRequest = lastRequestRef.current;
    if (!lastRequest) return;

    await runComposition(lastRequest);
  }, [runComposition]);

  const dismissAiGenerationError = useCallback(() => {
    setAiGenerationError(null);
  }, []);

  const resetAiAssistant = useCallback(
    ({ preserveMessageId }: { preserveMessageId?: boolean } = {}) => {
      setIsGeneratingAiSuggestion(false);
      setAiGenerationError(null);
      generationSequenceRef.current += 1;
      lastRequestRef.current = null;
      setPendingAiSuggestion('');
      setCurrentUserInput('');
      setSelectedPrompt('');
      setCustomPrompt('');
      setPreviousResponseId('');
      setSelectedPromptName('');
      if (!preserveMessageId) {
        setConversationMessageId(undefined);
      }
    },
    [setConversationMessageId],
  );

  useLayoutEffect(() => {
    resetAiAssistant();
  }, [generationScope, resetAiAssistant]);

  const handleDismissAiSuggestion = useCallback(() => {
    if (!setContent) return;
    // currentUserInput is the editor HTML captured when generation started, not
    // markdown — restore it verbatim; running it through the markdown parser
    // would inject spacer paragraphs between the user's own lines.
    setContent(currentUserInput);
    resetAiAssistant();
  }, [currentUserInput, resetAiAssistant, setContent]);

  const handleAcceptAiSuggestion = useCallback(() => {
    resetAiAssistant({ preserveMessageId: true });
  }, [resetAiAssistant]);

  return {
    isGeneratingAiSuggestion,
    pendingAiSuggestion,
    currentUserInput,
    selectedPrompt,
    customPrompt,
    handleRefineSuggestion,
    handleDismissAiSuggestion,
    handleAcceptAiSuggestion,
    resetAiAssistant,
    getInitialSuggestion,
    conversationMessageId,
    selectedPromptName: selectedPromptName || customPrompt || '',
    aiGenerationError,
    retryGeneration,
    dismissAiGenerationError,
  };
};
